import React, { createContext, useContext, useEffect, useState } from 'react';
import { login as apiLogin } from '../services/api';

const AuthContext = createContext(null);

const TOKEN_KEY = 'token';
const ROLE_KEY = 'userRole';

function readStoredSession() {
  const token = localStorage.getItem(TOKEN_KEY) || localStorage.getItem('authToken');
  if (!token) {
    return { isAuthenticated: false, userRole: null };
  }
  // Migrate legacy social-auth key onto the canonical token key.
  if (!localStorage.getItem(TOKEN_KEY) && localStorage.getItem('authToken')) {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.removeItem('authToken');
  }
  return {
    isAuthenticated: true,
    userRole: localStorage.getItem(ROLE_KEY) || null,
  };
}

export const AuthProvider = ({ children }) => {
  const initial = readStoredSession();
  const [isAuthenticated, setIsAuthenticated] = useState(initial.isAuthenticated);
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(initial.userRole);
  const [authReady, setAuthReady] = useState(false);

  useEffect(() => {
    // Re-read once on mount in case another tab wrote the token.
    const session = readStoredSession();
    setIsAuthenticated(session.isAuthenticated);
    setUserRole(session.userRole);
    setAuthReady(true);
  }, []);

  const login = async (username, password) => {
    try {
      const response = await apiLogin(username, password);
      setIsAuthenticated(true);
      setUser(response);
      setUserRole(response.role);
      localStorage.setItem(TOKEN_KEY, response.token);
      localStorage.setItem(ROLE_KEY, response.role);
      localStorage.removeItem('authToken');
      return true;
    } catch (error) {
      console.error('Login failed:', error);
      if (!error.response) {
        throw new Error(
          'Cannot reach the API (connection refused). Start the Django server on port 8000, ' +
            'for example: ./scripts/dev-local.sh or python manage.py runserver 8000'
        );
      }
      return false;
    }
  };

  const logout = async () => {
    setIsAuthenticated(false);
    setUser(null);
    setUserRole(null);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(ROLE_KEY);
    localStorage.removeItem('authToken');
  };

  return (
    <AuthContext.Provider
      value={{ isAuthenticated, authReady, user, userRole, login, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
